import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { spbPdfUrl, formatApiError, fileUrl } from "@/lib/api";
import { formatIdDateTime, formatPlate } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Printer,
  DownloadSimple,
  UploadSimple,
  Camera,
  X,
  MagnifyingGlassPlus,
  MagnifyingGlassMinus,
  ArrowsClockwise,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { devDebug } from "@/lib/debug";
import MismatchCorrectionPanel from "@/components/MismatchCorrectionPanel";
import { compressImage } from "@/lib/imageCompress";
import MasterOverridesPanel from "./spbDetail/MasterOverridesPanel";
import QRSignaturesPanel from "./spbDetail/QRSignaturesPanel";
import HistoryPanel from "./spbDetail/HistoryPanel";
import PlateOverridesPanel from "./spbDetail/PlateOverridesPanel";
import BuktiMuatPanel from "./spbDetail/BuktiMuatPanel";
import SPBHeader from "./spbDetail/SPBHeader";
import SPBContent from "./spbDetail/SPBContent";
import SPBActions from "./spbDetail/SPBActions";

export default function SPBDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [spb, setSpb] = useState(null);
  const [proofs, setProofs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadPhase, setUploadPhase] = useState(null); // "compress" | "upload" | null
  const [uploadPct, setUploadPct] = useState(0);
  const [proofBlobs, setProofBlobs] = useState({});
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [lightbox, setLightbox] = useState(null); // {blob, proof}
  const [zoom, setZoom] = useState(1); // 1..6
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [reprintBusy, setReprintBusy] = useState(false);
  const dragRef = React.useRef({ dragging: false, sx: 0, sy: 0, ox: 0, oy: 0 });

  // Reset zoom whenever lightbox opens/closes/changes photo
  useEffect(() => { setZoom(1); setPan({ x: 0, y: 0 }); setDragging(false); }, [lightbox?.proof?.id]);

  // Revoke blob URL when lightbox closes / photo changes to avoid memory growth
  useEffect(() => {
    return () => {
      if (lightbox?.blob && lightbox.blob.startsWith("blob:")) {
        try { URL.revokeObjectURL(lightbox.blob); }
        catch (err) {
          // revokeObjectURL never throws in practice, but log defensively so
          // future regressions surface instead of silently leaking blobs.
          devDebug("[SPBDetail] revokeObjectURL failed:", err);
        }
      }
    };
  }, [lightbox?.blob]);

  const clampZoom = (z) => Math.min(6, Math.max(1, z));
  const onWheelZoom = (e) => {
    e.preventDefault();
    const delta = -e.deltaY * 0.0015;
    setZoom((z) => clampZoom(z + delta * z));
  };
  const onImgMouseDown = (e) => {
    if (zoom <= 1) return;
    dragRef.current = { dragging: true, sx: e.clientX, sy: e.clientY, ox: pan.x, oy: pan.y };
    setDragging(true);
    e.preventDefault();
  };
  const onImgMouseMove = (e) => {
    if (!dragRef.current.dragging) return;
    const { sx, sy, ox, oy } = dragRef.current;
    setPan({ x: ox + (e.clientX - sx), y: oy + (e.clientY - sy) });
  };
  const onImgMouseUp = () => { dragRef.current.dragging = false; setDragging(false); };
  // Simple pinch support
  const touchRef = React.useRef({ dist: 0, zoom: 1 });
  const onImgTouchStart = (e) => {
    if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      touchRef.current = { dist: Math.hypot(dx, dy), zoom };
    } else if (e.touches.length === 1 && zoom > 1) {
      dragRef.current = { dragging: true, sx: e.touches[0].clientX, sy: e.touches[0].clientY, ox: pan.x, oy: pan.y };
    }
  };
  const onImgTouchMove = (e) => {
    if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const d = Math.hypot(dx, dy);
      if (touchRef.current.dist) {
        setZoom(clampZoom(touchRef.current.zoom * (d / touchRef.current.dist)));
      }
      e.preventDefault();
    } else if (e.touches.length === 1 && dragRef.current.dragging) {
      const { sx, sy, ox, oy } = dragRef.current;
      setPan({ x: ox + (e.touches[0].clientX - sx), y: oy + (e.touches[0].clientY - sy) });
    }
  };
  const onImgTouchEnd = () => { dragRef.current.dragging = false; setDragging(false); touchRef.current.dist = 0; };
  const resetZoom = () => { setZoom(1); setPan({ x: 0, y: 0 }); };

  const load = async () => {
    const { data } = await api.get(`/spb/${id}`);
    setSpb(data);
    const { data: p } = await api.get(`/spb/${id}/proofs`);
    setProofs(p);
    try {
      const { data: h } = await api.get(`/spb/${id}/history`);
      setHistory(h);
    } catch (err) { devDebug("failed to load spb history", err); }
  };

  // "Cetak Ulang QR" handler — for OLD SPBs created before the auto-sign
  // hook existed, `qr_signatures` is empty and the reprinted PDF would come
  // out with both signature boxes blank. This backfills a PEMBUAT-role
  // signature using the current user, refreshes local state, then opens
  // the fresh PDF in a new tab (with cache-buster).
  const handleReprintQR = async () => {
    if (reprintBusy) return;
    setReprintBusy(true);
    try {
      const { data } = await api.post(`/spb/${id}/qr-ensure-pembuat`);
      if (data?.created) {
        toast.success("QR PEMBUAT SPB ditambahkan — PDF akan dibuka.");
        await load();
      }
      // Rebuild PDF URL from spb (may be stale by one tick after load, but
      // the cache-buster below guarantees fresh bytes either way).
      const base = spbPdfUrl(id, Date.now());
      const url = `${base}${base.includes("?") ? "&" : "?"}reprint=${Date.now()}`;
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setReprintBusy(false);
    }
  };

  useEffect(() => {
    load();
    // Bootstrap when id changes. `load` closes over id + stable setters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Load proof images as blobs so they display
  useEffect(() => {
    proofs.forEach((p) => {
      if (proofBlobs[p.id]) return;
      api.get(`/files/${p.storage_path}`, { responseType: "blob" })
        .then((r) => {
          const url = URL.createObjectURL(r.data);
          setProofBlobs((b) => ({ ...b, [p.id]: url }));
        })
        .catch((err) => { devDebug("Proof image fetch failed", p.id, err); });
    });
    // Trigger when the list changes; skip already-loaded blobs via check above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proofs]);

  // Revoke all proof blob URLs on unmount to avoid a slow-leak of memory
  // when navigating between multiple SPBs. Uses a ref so we always see the
  // latest map without re-registering the effect.
  const proofBlobsRef = React.useRef(proofBlobs);
  useEffect(() => { proofBlobsRef.current = proofBlobs; }, [proofBlobs]);
  useEffect(() => () => {
    Object.values(proofBlobsRef.current || {}).forEach((u) => {
      if (typeof u === "string" && u.startsWith("blob:")) {
        try { URL.revokeObjectURL(u); } catch (err) { devDebug("[SPBDetail] revoke on unmount failed:", err); }
      }
    });
  }, []);

  const onUploadProof = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadPhase("compress");
    setUploadPct(0);
    const compressed = await compressImage(file);
    const fd = new FormData();
    fd.append("file", compressed);
    fd.append("kind", "bukti_muat");
    setUploadPhase("upload");
    try {
      await api.post(`/spb/${id}/proof`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) setUploadPct(Math.round((evt.loaded * 100) / evt.total));
        },
      });
      toast.success("Bukti muat berhasil diupload");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      setUploadPhase(null);
      setUploadPct(0);
      e.target.value = "";
    }
  };

  const setStatus = async (status, extra = {}) => {
    try {
      await api.post(`/spb/${id}/status`, { status, ...extra });
      toast.success(`Status diubah menjadi ${status}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // ─── Bukti Muat actions ────────────────────────────────────────────
  const downloadProof = (proof) => {
    const blob = proofBlobs[proof.id];
    if (!blob) {
      toast.error("Foto masih di-load…");
      return;
    }
    const a = document.createElement("a");
    a.href = blob;
    const ext = (proof.file_path || "").split(".").pop() || "jpg";
    const stamp = (proof.created_at || "").replace(/[:.]/g, "-").slice(0, 19);
    a.download = `${spb?.no_spb || "spb"}-bukti-${stamp}.${ext}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const printProof = (proof) => {
    const blob = proofBlobs[proof.id];
    if (!blob) {
      toast.error("Foto masih di-load…");
      return;
    }
    const w = window.open("", "_blank", "width=800,height=900");
    if (!w) {
      toast.error("Popup diblokir browser. Izinkan popup untuk cetak.");
      return;
    }
    const doc = w.document;
    doc.title = `Bukti Muat ${spb?.no_spb || ""} — ${proof.uploaded_by_name || ""}`;

    // Base document skeleton (CSS-only content — no user input reaches
    // innerHTML). Replaces the previous `document.write` call flagged by the
    // static analyzer even though we already escape below.
    doc.documentElement.lang = "id";
    const meta = doc.createElement("meta"); meta.setAttribute("charset", "utf-8");
    doc.head.appendChild(meta);
    const style = doc.createElement("style");
    style.textContent = `
      @page { size: auto; margin: 12mm; }
      body { font-family: system-ui, sans-serif; margin: 0; padding: 16px; }
      .header { border-bottom: 2px solid #000; padding-bottom: 8px; margin-bottom: 12px; }
      .header h1 { margin: 0; font-size: 18px; }
      .meta { font-size: 12px; color: #555; margin-top: 4px; }
      .meta div { margin: 2px 0; }
      img { max-width: 100%; height: auto; display: block; margin: 12px auto; page-break-inside: avoid; }
      .footer { margin-top: 12px; font-size: 10px; color: #888; text-align: center; }`;
    doc.head.appendChild(style);

    // All dynamic strings are inserted via `textContent` (never innerHTML),
    // so the browser DOM handles escaping and we cannot leak XSS via SPB
    // fields even if a future refactor forgets to sanitize.
    const setText = (tag, cls, text) => {
      const el = doc.createElement(tag);
      if (cls) el.className = cls;
      el.textContent = text ?? "";
      return el;
    };
    const inlineBold = (label, value) => {
      const wrap = doc.createElement("div");
      const b = doc.createElement("strong"); b.textContent = label;
      wrap.appendChild(b);
      wrap.appendChild(doc.createTextNode(" " + (value ?? "-")));
      return wrap;
    };

    const header = doc.createElement("div"); header.className = "header";
    header.appendChild(setText("h1", "", `BUKTI MUAT — ${spb?.no_spb || "-"}`));
    const metaWrap = doc.createElement("div"); metaWrap.className = "meta";
    const row1 = doc.createElement("div");
    const s1 = doc.createElement("strong"); s1.textContent = "Sopir:"; row1.appendChild(s1);
    row1.appendChild(doc.createTextNode(" " + (spb?.sopir_nama || "-") + " · "));
    const s2 = doc.createElement("strong"); s2.textContent = "Kendaraan:"; row1.appendChild(s2);
    row1.appendChild(doc.createTextNode(" " + (spb?.plat_nomor || "-")));
    metaWrap.appendChild(row1);
    const row2 = doc.createElement("div");
    const p1 = doc.createElement("strong"); p1.textContent = "Produk:"; row2.appendChild(p1);
    row2.appendChild(doc.createTextNode(" " + (spb?.produk_nama || "-") + " · "));
    const p2 = doc.createElement("strong"); p2.textContent = "Jumlah:"; row2.appendChild(p2);
    row2.appendChild(doc.createTextNode(
      " " + (spb?.jumlah_termuat ?? spb?.jumlah ?? "-") + " " + (spb?.satuan || "")));
    metaWrap.appendChild(row2);
    metaWrap.appendChild(inlineBold("Diupload oleh:",
      (proof.uploaded_by_name || "-") + " · " + (formatIdDateTime(proof.created_at) || "")));
    header.appendChild(metaWrap);
    doc.body.appendChild(header);

    // Image src set via property (never via string interpolation into HTML)
    const img = doc.createElement("img");
    img.src = blob;
    img.alt = "bukti muat";
    doc.body.appendChild(img);

    doc.body.appendChild(setText("div", "footer",
      "Dicetak: " + new Date().toLocaleString("id-ID")));

    // Trigger print via listener rather than injecting a <script> tag.
    if (doc.readyState === "complete") {
      setTimeout(() => { try { w.print(); } catch (err) { devDebug("print err", err); } }, 300);
    } else {
      w.addEventListener("load", () => {
        setTimeout(() => { try { w.print(); } catch (err) { devDebug("print err", err); } }, 300);
      });
    }
  };

  // ESC key closes lightbox
  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox]);

  const onCancel = async () => {
    const reason = window.prompt("Alasan pembatalan (opsional):", "");
    if (reason === null) return; // user closed dialog
    try {
      await api.post(`/spb/${id}/cancel`, { reason });
      toast.success("SPB dibatalkan");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const onDelete = async () => {
    if (!window.confirm(`Hapus SPB ${spb?.no_spb} secara permanen?\nRiwayat audit tetap tersimpan, tapi data SPB akan hilang.`)) return;
    try {
      await api.delete(`/spb/${id}`);
      toast.success("SPB dihapus");
      navigate("/spb");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const onRestore = async () => {
    if (!window.confirm(`Restore SPB ${spb?.no_spb}? Status akan kembali ke Belum Termuat.`)) return;
    try {
      await api.post(`/spb/${id}/restore`);
      toast.success("SPB direstore");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // Admin-only: replace no_polisi on a SPB that has already been loaded.
  // Requires typed reason for audit trail.
  const [plateDialog, setPlateDialog] = useState(false);
  const [plateForm, setPlateForm] = useState({ no_polisi: "", reason: "" });
  const [plateSaving, setPlateSaving] = useState(false);

  // QR signature — anyone signed-in can add their signature (admin, admin_kantor, petugas).
  const [qrSigning, setQrSigning] = useState(false);

  const submitQrSign = async (label) => {
    setQrSigning(true);
    try {
      await api.post(`/spb/${id}/qr-sign`, { role_label: (label || "").trim() });
      toast.success("SPB berhasil ditandatangani. QR sudah tersedia di panel di bawah.");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setQrSigning(false);
    }
  };

  const openPlateDialog = () => {
    setPlateForm({ no_polisi: spb?.no_polisi || "", reason: "" });
    setPlateDialog(true);
  };
  const submitPlateOverride = async (e) => {
    e.preventDefault();
    const plat = formatPlate(plateForm.no_polisi || "");
    const reason = (plateForm.reason || "").trim();
    if (!plat) return toast.error("Plat baru wajib diisi");
    if (plat === spb.no_polisi) return toast.error("Plat baru sama dengan plat lama");
    if (reason.length < 3) return toast.error("Alasan wajib diisi (min. 3 karakter)");
    setPlateSaving(true);
    try {
      await api.patch(`/spb/${id}/plate`, { no_polisi: plat, reason });
      toast.success(`Plat berhasil diubah dari ${spb.no_polisi} ke ${plat}`);
      setPlateDialog(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPlateSaving(false);
    }
  };

  if (!spb) return <div className="p-8 overline">Memuat...</div>;

  const pdfHref = spbPdfUrl(id, spb.updated_at || spb.created_at);

  return (
    <div className="p-6 md:p-8 max-w-6xl">
      <SPBHeader
        spb={spb}
        id={id}
        user={user}
        pdfHref={pdfHref}
        reprintBusy={reprintBusy}
        onReprintQR={handleReprintQR}
      />

      {/* PDF Preview */}
      <div className="mb-8 border border-border bg-card no-print">
        <div className="border-b border-border p-3 overline">Preview Surat</div>
        <iframe
          src={pdfHref}
          title="SPB PDF"
          className="w-full h-[600px] bg-white"
          data-testid="spb-pdf-iframe"
        />
      </div>

      {/* Detail Grid + Realisasi */}
      <SPBContent spb={spb} />

      {/* Mismatch Correction Workflow */}
      <MismatchCorrectionPanel spb={spb} onSpbUpdated={load} />

      {/* Riwayat Ganti Plat (admin plate overrides) */}
      <PlateOverridesPanel overrides={spb.plate_overrides} />

      {/* Riwayat Ganti Master (produk / plant / distrik) */}
      <MasterOverridesPanel overrides={spb.master_overrides} />

      {/* QR-based Tanda Tangan */}
      <QRSignaturesPanel
        spb={spb}
        spbId={id}
        signing={qrSigning}
        onSign={submitQrSign}
      />

      {/* Riwayat Perubahan */}
      <HistoryPanel
        history={history}
        open={historyOpen}
        onToggle={() => setHistoryOpen((o) => !o)}
      />

      {/* Bukti Muat */}
      <BuktiMuatPanel
        proofs={proofs}
        proofBlobs={proofBlobs}
        uploading={uploading}
        uploadPhase={uploadPhase}
        uploadPct={uploadPct}
        onUploadProof={onUploadProof}
        onOpenLightbox={setLightbox}
        onPrintProof={printProof}
        onDownloadProof={downloadProof}
      />

      {/* Lightbox modal untuk perbesar bukti muat */}
      {/* Admin plate-override dialog (post-loading correction) */}
      <Dialog open={plateDialog} onOpenChange={setPlateDialog}>
        <DialogContent className="max-w-md" data-testid="plate-override-dialog">
          <DialogHeader>
            <DialogTitle>Ganti No Polisi</DialogTitle>
            <DialogDescription>
              Ubah plat pada SPB yang sudah termuat. Dicatat di audit trail. Hanya administrator utama yang bisa menggunakan fitur ini.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitPlateOverride} className="space-y-3">
            <div>
              <Label className="overline">Plat Lama</Label>
              <div className="font-mono font-bold text-lg tabular-nums">{spb.no_polisi}</div>
            </div>
            <div>
              <Label htmlFor="new-plate" className="overline">Plat Baru</Label>
              <Input
                id="new-plate"
                value={plateForm.no_polisi}
                onChange={(e) => setPlateForm({ ...plateForm, no_polisi: formatPlate(e.target.value) })}
                placeholder="BK1234AB"
                className="rounded-lg font-mono font-bold"
                data-testid="plate-override-new-input"
                autoFocus
              />
            </div>
            <div>
              <Label htmlFor="plate-reason" className="overline">Alasan Penggantian *</Label>
              <Textarea
                id="plate-reason"
                value={plateForm.reason}
                onChange={(e) => setPlateForm({ ...plateForm, reason: e.target.value })}
                placeholder="mis. Truk pecah ban, ganti unit di tengah jalan"
                rows={3}
                className="rounded-lg"
                data-testid="plate-override-reason-input"
              />
              <p className="text-xs text-muted-foreground mt-1">Alasan disimpan di audit trail (min. 3 karakter).</p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPlateDialog(false)} disabled={plateSaving}>
                Batal
              </Button>
              <Button type="submit" disabled={plateSaving} data-testid="plate-override-submit-btn">
                {plateSaving ? "Menyimpan…" : "Ganti Plat"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>


      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex flex-col items-center justify-center p-4 no-print"
          onClick={() => setLightbox(null)}
          data-testid="proof-lightbox"
        >
          <div className="absolute top-4 right-4 flex gap-2">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setZoom((z) => clampZoom(z + 0.5)); }}
              className="bg-white/10 hover:bg-white/20 text-white p-2 backdrop-blur"
              aria-label="Zoom in"
              title="Zoom in (scroll)"
              data-testid="lightbox-zoom-in"
            >
              <MagnifyingGlassPlus size={20} />
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setZoom((z) => clampZoom(z - 0.5)); }}
              className="bg-white/10 hover:bg-white/20 text-white p-2 backdrop-blur disabled:opacity-40"
              disabled={zoom <= 1}
              aria-label="Zoom out"
              data-testid="lightbox-zoom-out"
            >
              <MagnifyingGlassMinus size={20} />
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); resetZoom(); }}
              className="bg-white/10 hover:bg-white/20 text-white p-2 backdrop-blur disabled:opacity-40"
              disabled={zoom === 1 && pan.x === 0 && pan.y === 0}
              aria-label="Reset zoom"
              title="Reset zoom"
              data-testid="lightbox-zoom-reset"
            >
              <ArrowsClockwise size={20} />
            </button>
            <div className="bg-white/10 text-white px-3 py-2 text-xs font-mono backdrop-blur tabular-nums">
              {Math.round(zoom * 100)}%
            </div>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); printProof(lightbox.proof); }}
              className="bg-white/10 hover:bg-white/20 text-white px-4 py-2 text-sm flex items-center gap-2 backdrop-blur"
              data-testid="lightbox-print"
            >
              <Printer size={16} />
              Cetak
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); downloadProof(lightbox.proof); }}
              className="bg-white/10 hover:bg-white/20 text-white px-4 py-2 text-sm flex items-center gap-2 backdrop-blur"
              data-testid="lightbox-download"
            >
              <DownloadSimple size={16} />
              Unduh
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
              className="bg-white/10 hover:bg-white/20 text-white p-2 backdrop-blur"
              aria-label="Tutup"
              data-testid="lightbox-close"
            >
              <X size={20} />
            </button>
          </div>
          <div
            className="flex items-center justify-center overflow-hidden select-none"
            style={{ width: "95vw", height: "80vh", touchAction: "none" }}
            onWheel={onWheelZoom}
            onMouseMove={onImgMouseMove}
            onMouseUp={onImgMouseUp}
            onMouseLeave={onImgMouseUp}
            onTouchStart={onImgTouchStart}
            onTouchMove={onImgTouchMove}
            onTouchEnd={onImgTouchEnd}
            onClick={(e) => e.stopPropagation()}
            onDoubleClick={(e) => { e.stopPropagation(); zoom > 1 ? resetZoom() : setZoom(2); }}
          >
            <img
              src={lightbox.blob}
              alt="bukti muat besar"
              draggable={false}
              onMouseDown={onImgMouseDown}
              className="max-w-full max-h-full object-contain will-change-transform"
              style={{
                transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                cursor: zoom > 1 ? (dragging ? "grabbing" : "grab") : "zoom-in",
                transition: dragging ? "none" : "transform 100ms ease-out",
              }}
            />
          </div>
          <div className="mt-4 text-white text-sm text-center font-mono no-print">
            <div>{lightbox.proof.uploaded_by_name}</div>
            <div className="text-white/60 text-xs">{formatIdDateTime(lightbox.proof.created_at)}</div>
            <div className="text-white/40 text-xs mt-2">Scroll untuk zoom · Klik+drag untuk geser · Double-click reset · ESC tutup</div>
          </div>
        </div>
      )}

      {/* Action Panel */}
      <SPBActions
        spb={spb}
        user={user}
        onSetStatus={setStatus}
        onCancel={onCancel}
        onDelete={onDelete}
        onRestore={onRestore}
        onOpenPlateDialog={openPlateDialog}
      />
    </div>
  );
}

